import { connect, useDispatch } from 'react-redux';
import { Form, FormRenderProps } from 'react-final-form';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dispatch } from 'redux';
import arrayMutators from 'final-form-arrays';

import {
  getPublishRulesActiveTreeItem,
  getPublishRulesFormData,
  getPublishRulesFormRef,
  getPublishRulesHistoryChanges,
  getPublishRulesTree,
} from 'PublishRulesPackage/MainModule/selectors/publishRulesSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { IKnmHistoryItemChanges } from 'KnmPackage/KnmHistoriesModule/interfaces/IKnmHistoryItemChanges';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { IPublishRules } from 'APIPackage/PublishRulesModule/interfaces/IPublishRules';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';

import { PublishRulesFormRef } from '../reducers/publishRulesReducer';
import {
  createOrUpdatePublishRules,
  deletePublishRules,
  loadPublishRulesData,
  setPublishRulesFormRef,
} from '../actions/publishRulesActions';
import { PUBLISH_RULES_FORM_ID } from '../constants/publishRulesFieldsElementsIds';
import { PublishRulesFormAdditionalSettings } from './PublishRulesFormAdditionalSettings';
import { IPublishRulesTreeItem } from '../interfaces/IPublishRulesTreeItem';
import { PublishRulesHistory } from './PublishRulesHistory';
import { PublishRulesFormLeavingGuard } from './PublishRulesFormLeavingGuard';
import { IPublishRulesErpScheduledForm } from '../interfaces/IPublishRulesErpScheduledForm';
import { IPublishRulesErpScheduledFormRef } from '../interfaces/IPublishRulesErpScheduledFormRef';
import { makeBlockTitle } from '../utils/makeBlockTitle';
import { getPublishRulesErpScheduledFormInitialValues } from '../common/getPublishRulesErpScheduledFormInitialValues';
import { getKnmReasonType } from '../utils/getKnmReasonType';
import { PublishRulesApproveTypeEnum } from '../enums/publishRulesApproveTypeEnum';
import { makePlannedValues } from '../common/makePlannedValues';

export type IPublishRulesErpScheduledFormRenderProps = FormRenderProps<IPublishRulesErpScheduledForm>;

interface IPublishRulesErpScheduledFormProps {
  activeTreeItem: IPublishRulesTreeItem;
  tree: IPublishRulesTreeItem[];
  formRef?: IPublishRulesErpScheduledFormRef;
  formData?: IPublishRules;
  historyChanges?: IKnmHistoryItemChanges[];
}

const PublishRulesErpScheduledFormWithoutStore = ({
  activeTreeItem,
  tree,
  formRef,
  formData,
  historyChanges,
}: IPublishRulesErpScheduledFormProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [isConfirmDeleteSettingsVisible, setIsConfirmDeleteSettingsVisible] = useState<boolean>(false);
  const [reasonTypesSelectOptions, setReasonTypesSelectOptions] = useState<ISelectOption[]>([]);

  const setFormRef = useCallback(
    (newFormRef?: PublishRulesFormRef) => dispatch(setPublishRulesFormRef(newFormRef)),
    [],
  );

  // Значения по умолчанию
  const initialValues = useMemo(() => getPublishRulesErpScheduledFormInitialValues(activeTreeItem), [
    activeTreeItem.category,
  ]);

  // Загрузка данных
  useEffect(() => {
    dispatch(loadPublishRulesData(activeTreeItem.category));
  }, [activeTreeItem.category]);

  // Инициализация полученными данными
  useEffect(() => {
    if (formData && formRef) {
      const { entries, ervkKnmReasonTypes } = formData;

      const reasonTypesSelectOptionsFromData = ervkKnmReasonTypes?.map(item => getKnmReasonType(item));

      reasonTypesSelectOptionsFromData && setReasonTypesSelectOptions(reasonTypesSelectOptionsFromData);

      if (entries?.length !== undefined && entries?.length > 0) {
        const entry = entries?.find(item => item.approveType === PublishRulesApproveTypeEnum.REQUIRED) || entries?.[0];

        const data: IPublishRulesErpScheduledForm = makePlannedValues(entry, reasonTypesSelectOptionsFromData || []);

        formRef.current.initialize(data);
      }
    }
  }, [formData, formRef]);

  const blockTitle = useMemo(() => makeBlockTitle(tree, activeTreeItem), [tree, activeTreeItem]);

  const handleFormSubmit = useCallback(
    (data: IPublishRulesErpScheduledForm) => {
      const newData: IPublishRules = {
        entries: [
          {
            approveType: null,
            publicationCategoryCode: activeTreeItem.category,
            settings: {
              mainRule: {
                publicationMode: data.publicationDate || null,
              },
              additionalRules:
                data.additionalSettings?.map(setting => ({
                  reasonTypeId: (setting.typeOfBasis?.id as number) || null,
                  rule: {
                    publicationMode: setting.publicationDate || null,
                  },
                })) || null,
            },
          },
        ],
      };

      dispatch(createOrUpdatePublishRules(activeTreeItem.category, newData));
    },
    [activeTreeItem.category],
  );

  const handleApplyDeleteSetting = useCallback(() => {
    dispatch(deletePublishRules(activeTreeItem.category, initialValues));
  }, [formRef, activeTreeItem.category, initialValues]);

  const handleOnClearSetting = useCallback(() => {
    setIsConfirmDeleteSettingsVisible(true);
  }, []);

  const renderForm = ({ handleSubmit, form }: FormRenderProps<IPublishRulesErpScheduledForm>) => {
    const currentFormRef = useRef<IPublishRulesErpScheduledFormRenderProps['form']>(form);

    useEffect(() => {
      if (currentFormRef) {
        setFormRef(currentFormRef);
      }
    }, [setFormRef, currentFormRef]);

    return (
      <form id={PUBLISH_RULES_FORM_ID} onSubmit={handleSubmit}>
        <KnmCollapse
          theme="fields"
          title={blockTitle}
          onClose={activeTreeItem.haveSavedSettings ? handleOnClearSetting : undefined}
          openByDefault
          isNew={!activeTreeItem.haveSavedSettings}
        >
          <>
            <FieldBlock label="Основное правило">
              <Row notWrap>
                <FieldBlock size="half" label="Дата публикации" placementLabel="left" color="black">
                  <DatePickerField
                    name="publicationDate"
                    placeholder="Выберите дату"
                    validate={makeRequired(REQUIRED_ERROR)}
                    dateFormat="dd.MM"
                    showCalendarYear={false}
                  />
                </FieldBlock>
              </Row>
            </FieldBlock>
            <PublishRulesFormAdditionalSettings
              showStatus={false}
              reasonTypesSelectOptions={reasonTypesSelectOptions}
            />
            {historyChanges && (
              <Row>
                <PublishRulesHistory historyChanges={historyChanges} />
              </Row>
            )}
          </>
        </KnmCollapse>
        <PublishRulesFormLeavingGuard formRef={formRef} />
      </form>
    );
  };

  return (
    <>
      <Form<IPublishRulesErpScheduledForm>
        onSubmit={handleFormSubmit}
        render={renderForm}
        mutators={{ ...arrayMutators }}
        initialValues={initialValues}
      />
      <ConfirmModal
        visible={isConfirmDeleteSettingsVisible}
        title="Подтверждение"
        text="Удалить текущие настройки?"
        onApply={handleApplyDeleteSetting}
        onClose={() => setIsConfirmDeleteSettingsVisible(false)}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  activeTreeItem: getPublishRulesActiveTreeItem(state),
  tree: getPublishRulesTree(state),
  formRef: getPublishRulesFormRef(state),
  formData: getPublishRulesFormData(state),
  historyChanges: getPublishRulesHistoryChanges(state),
});

export const PublishRulesErpScheduledForm = connect(mapStateToProps)(PublishRulesErpScheduledFormWithoutStore);
